// src/lib/authLinkError.js
// Issue #547, Session 79.14e — turning Supabase's refusal of an email link into
// something a person can act on.
//
// ── WHAT WENT WRONG, in plain words ──────────────────────────────────────────
//
// A new hire presses "Set your password" in their welcome email. The token has
// already been used, so Supabase refuses it and bounces them to Stede's sign-in
// screen carrying a fragment:
//
//   #error=access_denied&error_code=otp_expired
//   &error_description=Email+link+is+invalid+or+has+expired
//
// Stede rendered a blank sign-in form and said NOTHING. The explanation was
// sitting in the address bar and no code in the app read it — a grep for
// `error_description` before this change returned one unrelated comment.
//
// The only conclusion available to the person is "the link is broken", which is
// exactly how it was reported from the field. That is the silent-refusal class
// this project already has a rule about: #492 (a Deny button that does nothing),
// #497 (a failed photo blamed for a lost count), #499 (a rejected work-order
// number surfacing nowhere). A control that refuses somebody must answer them.
//
// ── WHY THE WORDING IS OURS RATHER THAN SUPABASE'S ───────────────────────────
//
// "Email link is invalid or has expired" states the fact and stops. It leaves
// the reader where they started, and worse, it invites the one thing that
// cannot work: opening the link again. These tokens are SINGLE-USE, so the
// remedy is always a NEW link, never a retry. The message therefore names the
// two ways to get one.
//
// Kept in its own module rather than inline in Login.js because the component
// cannot be rendered under this project's Jest setup — react-router-dom v7's
// exports map defeats CRA's resolver — so this is the half that can be pinned
// by tests. The WIRING is proven live in the browser instead; see #547.

// The message to show, or '' when the URL says nothing worth saying.
//
// Takes the raw fragment, with or without its leading '#'. Returns '' for an
// empty hash and for a SUCCESSFUL recovery link — that one carries
// `type=recovery` and an access token and no error at all, and supabase-js
// still needs it. Matching on "there is a hash" rather than on "there is an
// error" would break every working invite in the course of explaining the
// broken ones.
export function authLinkErrorMessage(hash) {
  const raw = String(hash == null ? '' : hash).replace(/^#/, '');
  if (!raw) return '';

  let params;
  try {
    params = new URLSearchParams(raw);
  } catch {
    return '';
  }

  const code = params.get('error_code');
  const desc = params.get('error_description');
  const err = params.get('error');
  if (!code && !desc && !err) return '';

  // The ordinary case, and the one that brought this issue in. Both a spent
  // token and a timed-out one arrive as otp_expired, and the person cannot tell
  // the difference — nor do they need to, because the remedy is identical.
  if (code === 'otp_expired' || /expired|invalid/i.test(desc || '')) {
    return 'That link has already been used, or it has expired. These links only work once. '
         + 'Use "Forgot password?" below to send yourself a new one, or ask your administrator '
         + 'to resend your invite.';
  }

  // Anything else: say what the server said rather than inventing a diagnosis,
  // but never leave them with nothing. `+` is a space in a URL-encoded value and
  // URLSearchParams has already decoded it; this catches a value that arrived
  // double-encoded rather than re-doing that work.
  const plain = (desc || '').replace(/[+]/g, ' ').trim();
  return plain
    || 'That link could not be used. Sign in below, or ask your administrator to resend your invite.';
}
